import { usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";

export const useCurrencyFormatter = () => {
    const currencySymbols = {
        'BRL': 'R$',
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'TES': 'T$',
        'CAD': 'C$',
        'AUD': 'A$',
        'CHF': 'CHF',
        'CNY': '¥',
        'ARS': '$'
    };

    const roundWithCents = (value) => {
        return Math.ceil(value * 20) / 20;
    };

    const getCurrentCurrency = () => {
        const currentPath = window.location.pathname;
        const context = currentPath.includes('/admin') ? 'admin' : 'client';
        const currencyKey = `currency.${context}`;

        return localStorage.getItem(currencyKey) || 'USD';
    };

    const page = usePage();
    const currencies = computed(() => page.props.currencies || []);
    const exchangeValues = computed(() => page.props.exchangeValues || []);

    const exchangeRates = computed(() => {
        const rates = {};

        if (page.props.currencies && page.props.exchangeValues) {
            currencies.value.forEach((currency, index) => {
                rates[currency] = exchangeValues.value[index];
            });
        }

        return rates;
    });

    const updateExchangeRates = (newCurrencies, newExchangeValues) => {
        if (newCurrencies && newExchangeValues) {
            page.props.currencies = newCurrencies;
            page.props.exchangeValues = newExchangeValues;
        }
    };


    const getCurrencySymbol = (currencyCode = null) => {
        const code = currencyCode || getCurrentCurrency();
        return currencySymbols[code] || code;
    };

    const currencyLocales = {
        'BRL': 'pt-BR',
        'USD': 'en-US',
        'EUR': 'de-DE',
        'GBP': 'en-GB',
    };

    const getLocaleForCurrency = (currencyCode) => {
        return currencyLocales[currencyCode] || 'en-US';
    };

    const formatWithSymbol = (value, currencyCode = null) => {
        if (!value && value !== 0) return '-';

        const roundedValue = roundWithCents(value);

        const currency = currencyCode || getCurrentCurrency();
        const symbol = getCurrencySymbol(currency);
        const locale = getLocaleForCurrency(currency);

        const formattedValue = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(roundedValue);

        return `${symbol} ${formattedValue}`;
    };

    const formatWithSymbolRaw = (value, currencyCode = null) => {
        if (!value && value !== 0) return '-';

        const currency = currencyCode || getCurrentCurrency();
        const symbol = getCurrencySymbol(currency);
        const locale = getLocaleForCurrency(currency);

        const formattedValue = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);

        return `${symbol} ${formattedValue}`;
    };

    const formatCents = (valueInCents, currencyCode = null) => {
        if (!valueInCents && valueInCents !== 0) return '-';
        return formatWithSymbol(valueInCents / 100, currencyCode);
    };

    const onCurrencyChange = (callback) => {
        const currentPath = window.location.pathname;
        const context = currentPath.includes('/admin') ? 'admin' : 'client';
        const currencyKey = `currency.${context}`;

        window.addEventListener('storage', (event) => {
            if (event.key === currencyKey && event.newValue) {
                callback({ currency: event.newValue, symbol: getCurrencySymbol(event.newValue) });
            }
        });

        window.addEventListener(`currency-changed-${context}`, (event) => {
            callback({ currency: event.detail.currency, symbol: getCurrencySymbol(event.detail.currency) });
        });

        window.addEventListener('currencyChanged', (event) => {
            if (event.detail.context === context || !event.detail.context) {
                callback(event.detail);
            }
        });
    };

    // Reactive currency ref — keeps formatConvertedPrice reactive to currency changes
    const _activeCurrency = ref(getCurrentCurrency());

    onCurrencyChange(({ currency }) => {
        _activeCurrency.value = currency;
    });

    const convertPrice = (priceInCents, fromCurrency, toCurrency = null) => {
        if (!priceInCents) return 0;

        const normalizedPrice = priceInCents / 100;
        const targetCurrency = toCurrency || getCurrentCurrency();

        if (fromCurrency === targetCurrency) return normalizedPrice;

        const fromRate = exchangeRates.value[fromCurrency] || 100;
        const toRate = exchangeRates.value[targetCurrency] || 100;

        return (normalizedPrice * toRate) / fromRate;
    };

    const formatConvertedPrice = (price, originalCurrency, originalPrice = null, originalPriceCoin = null) => {
        if (!price || !originalCurrency) return '--';

        const targetCurrency = _activeCurrency.value;
        if (!targetCurrency) return '--';

        // If original price is provided and target currency matches the original coin,
        // use original price directly to avoid double conversion (BRL->USD->BRL)
        if (originalPrice && originalPriceCoin && targetCurrency === originalPriceCoin) {
            return formatWithSymbol(originalPrice / 100, originalPriceCoin);
        }

        if (!exchangeRates.value || Object.keys(exchangeRates.value).length === 0) {
            return formatWithSymbol(price / 100, originalCurrency);
        }

        const convertedPrice = convertPrice(price, originalCurrency, targetCurrency);

        if (!convertedPrice || isNaN(convertedPrice)) {
            return '--';
        }

        return formatWithSymbol(convertedPrice, targetCurrency);
    };

    return {
        getCurrentCurrency,
        getCurrencySymbol,
        formatWithSymbol,
        formatWithSymbolRaw,
        formatCents,
        formatConvertedPrice,
        onCurrencyChange,
        roundWithCents,
        exchangeRates,
        updateExchangeRates,
        convertPrice
    };
};
